// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import React, { useState } from "react";
import { supabase } from "../lib/supabase";
import { DARK_THEME as T } from "../lib/theme";

interface PrivacyModalProps {
  /** Called when the user closes the modal without deleting their account. */
  onClose: () => void;
  /** Called after a successful account deletion so the parent can sign the user out. */
  onAccountDeleted: () => void;
  /** Optional — if provided, the storage provider is used directly (avoids re-instantiation). */
  exportMyData?: () => Promise<{ data?: unknown; error?: string }>;
  deleteMyData?: () => Promise<{ error?: string }>;
}

type Stage =
  | "menu"
  | "exporting"
  | "exported"
  | "confirm-delete"
  | "deleting"
  | "deleted"
  | "error";

export const PrivacyModal: React.FC<PrivacyModalProps> = ({
  onClose,
  onAccountDeleted,
  exportMyData,
  deleteMyData,
}) => {
  const [stage, setStage] = useState<Stage>("menu");
  const [errorMsg, setErrorMsg] = useState("");

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setStage("exporting");
    try {
      let result: { data?: unknown; error?: string };
      if (exportMyData) {
        result = await exportMyData();
      } else if (supabase) {
        const { data, error } = await supabase.rpc("export_my_data");
        result = error ? { error: error.message } : { data };
      } else {
        result = { error: "Not connected" };
      }

      if (result.error) {
        setErrorMsg(result.error);
        setStage("error");
        return;
      }

      // Trigger browser download
      const blob = new Blob([JSON.stringify(result.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `openestimate-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);

      setStage("exported");
    } catch (e: any) {
      setErrorMsg(e?.message || "Unexpected error");
      setStage("error");
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setStage("deleting");
    try {
      let result: { error?: string };
      if (deleteMyData) {
        result = await deleteMyData();
      } else if (supabase) {
        // Server endpoint runs delete_my_data() RPC AND auth.admin.deleteUser()
        // (the client cannot call deleteUser — it requires the service_role key)
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          result = { error: "Session expired. Please sign in again." };
        } else {
          const delRes = await fetch("/api/account?action=delete", {
            method: "POST",
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          const delData: { error?: string } = await delRes.json().catch(() => ({}));
          result = delRes.ok ? {} : { error: delData.error || "Failed to delete account" };
        }
      } else {
        result = { error: "Not connected" };
      }

      if (result.error) {
        setErrorMsg(result.error);
        setStage("error");
        return;
      }

      // Sign out
      if (supabase) await supabase.auth.signOut();
      setStage("deleted");
      setTimeout(onAccountDeleted, 2000);
    } catch (e: any) {
      setErrorMsg(e?.message || "Unexpected error");
      setStage("error");
    }
  };

  // ── Styles ───────────────────────────────────────────────────────────────────
  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, zIndex: 9999,
    background: T.bgModalBackdrop,
    display: "flex", alignItems: "center", justifyContent: "center",
  };
  const modal: React.CSSProperties = {
    background: T.bgModal,
    border: `1px solid ${T.borderSecondary}`,
    borderRadius: 10,
    padding: "32px 36px",
    width: 480,
    maxWidth: "calc(100vw - 32px)",
    boxShadow: "0 24px 64px #000a",
    fontFamily: "'Inter', 'Avenir Next', 'Century Gothic', sans-serif",
    color: T.textPrimary,
  };
  const title: React.CSSProperties = {
    margin: "0 0 6px",
    fontSize: 16,
    fontWeight: 600,
    color: T.textPrimary,
  };
  const subtitle: React.CSSProperties = {
    margin: "0 0 24px",
    fontSize: 13,
    color: T.textMuted,
    lineHeight: 1.5,
  };
  const sectionTitle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: T.textTertiary,
    marginBottom: 12,
  };
  const card: React.CSSProperties = {
    background: T.bgPanel,
    border: `1px solid ${T.borderPrimary}`,
    borderRadius: 10,
    padding: "16px 18px",
    marginBottom: 16,
  };
  const cardTitle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 600,
    color: T.textPrimary,
    marginBottom: 4,
  };
  const cardDesc: React.CSSProperties = {
    fontSize: 12,
    color: T.textMuted,
    lineHeight: 1.5,
    marginBottom: 14,
  };
  const btn = (variant: "primary" | "danger" | "ghost"): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "7px 16px",
    borderRadius: 6,
    border: variant === "ghost" ? `1px solid ${T.borderSecondary}` : "none",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    background:
      variant === "primary" ? "#4fd1c5" :
      variant === "danger"  ? "#d4706a" :
      "transparent",
    color:
      variant === "primary" ? T.textOnGold :
      variant === "danger"  ? "#fff" :
      T.textSecondary,
    transition: "opacity 0.15s",
  });
  const dangerZone: React.CSSProperties = {
    borderTop: `1px solid ${T.borderPrimary}`,
    marginTop: 8,
    paddingTop: 20,
  };
  const row: React.CSSProperties = {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  };
  const closeBtn: React.CSSProperties = {
    position: "absolute" as const,
    top: 12, right: 14,
    background: "none",
    border: "none",
    color: T.textMuted,
    cursor: "pointer",
    fontSize: 20,
    lineHeight: 1,
    padding: "2px 6px",
  };
  const wrapperStyle: React.CSSProperties = { position: "relative" };

  // ── Render states ─────────────────────────────────────────────────────────
  if (stage === "deleted") {
    return (
      <div style={overlay}>
        <div style={{ ...modal, textAlign: "center", padding: "48px 36px" }}>
          <div style={{ fontSize: 32, marginBottom: 16 }}>✓</div>
          <p style={{ ...title, textAlign: "center" }}>Account deleted</p>
          <p style={{ ...subtitle, textAlign: "center" }}>
            Your data has been erased. You will be signed out shortly.
          </p>
        </div>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div style={overlay}>
        <div style={{ ...modal, ...wrapperStyle }}>
          <button style={closeBtn} onClick={onClose}>×</button>
          <p style={title}>Something went wrong</p>
          <p style={{ ...subtitle, color: T.negative }}>{errorMsg}</p>
          <button style={btn("ghost")} onClick={() => setStage("menu")}>← Back</button>
        </div>
      </div>
    );
  }

  if (stage === "confirm-delete") {
    return (
      <div style={overlay}>
        <div style={{ ...modal, ...wrapperStyle }}>
          <button style={closeBtn} onClick={onClose}>×</button>
          <p style={{ ...title, color: T.negative }}>⚠ Delete your account?</p>
          <p style={subtitle}>
            This is <strong>permanent and irreversible.</strong> All projects you own will be
            soft-deleted, your org memberships removed, and your profile anonymised. This action
            cannot be undone.
          </p>
          <div style={{ ...card, background: "#3d1a1a", borderColor: "#bf6a4a40" }}>
            <p style={{ margin: 0, fontSize: 13, color: "#e8a07a", lineHeight: 1.5 }}>
              We recommend downloading a data export first so you have a copy of your work.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <button
              style={btn("ghost")}
              onClick={() => setStage("menu")}
            >Cancel</button>
            <button
              style={{ ...btn("danger"), marginLeft: "auto" }}
              onClick={handleDelete}
            >
              Yes, delete my account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main menu (and loading states) ────────────────────────────────────────
  const isLoading = stage === "exporting" || stage === "deleting";

  return (
    <div style={overlay}>
      <div style={{ ...modal, ...wrapperStyle }}>
        <button style={closeBtn} onClick={onClose} disabled={isLoading}>×</button>

        <p style={title}>Privacy &amp; Data Controls</p>
        <p style={subtitle}>
          You own your data. Export a copy at any time, or permanently delete your account and
          all associated information.
        </p>

        <p style={sectionTitle}>Data portability (GDPR Art. 20)</p>
        <div style={card}>
          <div style={row}>
            <div>
              <p style={cardTitle}>Download my data</p>
              <p style={{ ...cardDesc, marginBottom: 0 }}>
                Download a JSON file containing your profile, all projects, snapshots, sharing
                history, and audit log.
              </p>
            </div>
            <button
              style={{ ...btn("primary"), whiteSpace: "nowrap", flexShrink: 0 }}
              onClick={handleExport}
              disabled={isLoading}
            >
              {stage === "exporting" ? "Exporting…" :
               stage === "exported"  ? "✓ Downloaded" :
               "Export data"}
            </button>
          </div>
        </div>

        <div style={dangerZone}>
          <p style={{ ...sectionTitle, color: "#d4706a80" }}>Danger zone</p>
          <div style={{ ...card, borderColor: "#bf6a4a40" }}>
            <div style={row}>
              <div>
                <p style={cardTitle}>Delete my account</p>
                <p style={{ ...cardDesc, marginBottom: 0 }}>
                  Permanently erases your profile, org memberships, and owned project data.
                  Cannot be undone.
                </p>
              </div>
              <button
                style={{ ...btn("danger"), whiteSpace: "nowrap", flexShrink: 0, opacity: isLoading ? 0.5 : 1 }}
                onClick={() => setStage("confirm-delete")}
                disabled={isLoading}
              >
                {stage === "deleting" ? "Deleting…" : "Delete account"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyModal;
